<template>
  <article v-if="roadtrip" class="road-trip--detail" itemscope itemtype="http://schema.org/ScholarlyArticle">
    <vue-headful :title="title" :description="description" :url="url" />

    <header class="container road-trip--detail-header">

      <div class="road-trip--badge">
        <span class="road-trip--badge-title">Trip</span>
        <span class="road-trip--badge-number">{{ roadtrip.tripNumber }}</span>
      </div>

      <nav aria-label="breadcrumbs">
        <b-breadcrumb>
          <b-breadcrumb-item v-for="breadcrumb in Breadcrumbs" :key="breadcrumb.id" :text="breadcrumb.text"
            :to="breadcrumb.href" :active="breadcrumb.active" />
        </b-breadcrumb>
      </nav>

      <div class="row">
        <div class="col-lg-6">
          <h1 class="road-trip--heading" itemprop="name">{{ roadtrip.title }}</h1>
          <h2 class="road-trip--author" itemprop="author">by {{ roadtrip.author }}</h2>

          <div class="road-trip--toc">
            <h3 class="road-trip--toc-h3">Table of Contents</h3>
            <ul id="toc">
            </ul>
          </div>
        </div>

        <div class="col-lg-6 road-trip--img">
          <img :src="roadtrip.mainImageUrl" :alt="roadtrip.title" class="img-fluid"
            @error='stagingImageBucketFallBack($event, roadtrip.mainImageUrl)'>
        </div>
      </div>

    </header>

    <div class="container" id="road-trip">
      <div :class="{ 'content-hidden': isHidden, 'content-displayed': !isHidden }">
        <div class="road-trip--content" ref="tripbody" data-swiftype-name="body" data-swiftype-type="text"
          v-html="this.roadtrip.body"></div>
      </div>
    </div>
    <div class="hidden-content-div">
      <div class="btn-center">
        <button class='road-trip--btn button' @click="toggleClass">
          <template v-if="isHidden">Read More
            <IconBase variant="plus" size="12" color="#FFF" classes="plus-icon-size" />
          </template>
          <template class='road-trip--btn' v-else>
            <a href="#road-trip">Read Less
              <IconBase variant="minus" size="12" color="#FFF" classes="plus-icon-size" />
            </a>
          </template>
        </button>
      </div>
    </div>

    <aside class="road-trip--directions">
      <div class="road-trip--map">
        <gmap-map ref="map" :center="mapCenter" :zoom="12" :options="{
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          disableDefaultUi: true,
          styles: styles
        }" style="width: 50vw; height: 50vh;">
          <gmap-marker :key="index" v-for="(m, index) in mapPointList" :position="m.position" :clickable="true"
            :draggable="false" @click="openWindow(m)" />
          <gmap-info-window @closeclick="window_open = false" :opened="window_open" :position="infowindow" :options="{
            pixelOffset: {
              width: 0,
              height: -35
            }
          }">
            <div>{{ locationName }}</div>
            <div><a :href="locationUrl">Read More</a></div>
            <div><a :href="mapUrl" target="_blank" rel="noopener noreferrer">Get Directions</a></div>
          </gmap-info-window>
        </gmap-map>
      </div>
      <div class="road-trip--about">
        <div v-html="roadtrip.about"></div>
        <div v-if="roadtrip.directions" v-html="roadtrip.directions"></div>
      </div>
    </aside>

    <div class="container">
      <div v-if="roadtrip.dontMiss" class="road-trip--dontmiss">
        <h2 class="sr-only">Don't Miss</h2>
        <figure class="road-trip--dontmiss-figure">
          <img class="road-trip--dontmiss-image" :src="roadtrip.dontMissImageUrl" alt="" role="presentation"
            @error='stagingImageBucketFallBack($event, roadtrip.dontMissImageUrl)'>
        </figure>
        <div v-html="roadtrip.dontMiss"></div>
      </div>
    </div>

    <TripIdeas headerText="Related Road Trips" :showRelated=true :relatedTrips="roadtrip.related_trips" />
    <Feedback pageType="trip" />
    <Footer />
  </article>
</template>

<script>
import styles from '@/assets/map_styling.json';
import Footer from '@/components/Common/Footer.vue';
import TripIdeas from '@/components/PlacesToGo/Common/TripIdeas.vue';
import axios from 'axios';
import {
  Component, Prop,
  Vue,
  Watch,
} from 'vue-property-decorator';
import IconBase from '../components/Common/icons/index.vue';

function setTOC() {
  const h3IDs = document.getElementsByClassName('road-trip--h3');
  let tocList = '';

  for (let x = 0; x < h3IDs.length; x++) {
    const title = h3IDs[x].innerHTML;
    const url = h3IDs[x].getAttribute('id');
    tocList = `${tocList}<li><a href="#${url}">${title}</a></li>`;
  }
  document.getElementById('toc').innerHTML = tocList;
}

@Component({
  components: {
    Footer,
    TripIdeas,
    IconBase,
  },
  data() {
    return {
      styles,
      roadtrip: null,
      mapPoints: [],
      info_marker: null,
      infowindow: { lat: 10, lng: 10.0 },
      window_open: false,
      locationName: '',
      locationUrl: '/places-to-go',
      mapUrl: '',
      title: '',
      description: '',
      url: '',
      isHidden: true,
      searchText: null,
    };
  },
  computed: {
    mapPointList() {
      this.mapPoints = [];
      this.roadtrip.map_points.forEach((trip) => {
        this.mapPoints.push({
          address: trip.address,
          slug: trip.slug,
          label: trip.name,
          position: { lat: parseFloat(trip.latitude), lng: parseFloat(trip.longitude) },
        });
      });
      return this.mapPoints;
    },
    mapCenter() {
      // Get the first map point to use as center or default
      if (this.roadtrip.map_points && this.roadtrip.map_points.length > 0) {
        return { lat: parseFloat(this.roadtrip.map_points[0].latitude), lng: parseFloat(this.roadtrip.map_points[0].longitude) };
      }
      return { lat: 33, lng: -68.0 };
    },
  },
  methods: {
    async stagingImageBucketFallBack(e, img) {
      // if initial onload image is not avail, we roll to the staging onload image.
      // if an image is still not found, we use a placeholder image. This  prevents
      // and endless loop on error because cant find image in production or staging.
      const modifiedLink = img.replace('alabama-travel', 'staging-alabama-travel');

      const imageExists = await this.testImage(modifiedLink);

      if (imageExists) {
        e.target.src = modifiedLink;
      } else {
        e.target.src = 'https://via.placeholder.com/200/300';
      }
    },
    testImage(url) {
      if (!url || typeof url !== 'string') return Promise.resolve(false);
      return new Promise((resolve) => {
        const imgElement = new Image();
        imgElement.addEventListener('load', () => resolve(true));
        imgElement.addEventListener('error', () => resolve(false));
        imgElement.src = url;
      });
    },
    openWindow(marker) {
      this.infowindow = { lat: marker.position.lat, lng: marker.position.lng };
      this.locationName = marker.label;
      this.locationUrl = `/places-to-go/${marker.slug}`;
      this.mapUrl = `https://www.google.com/maps/dir/${encodeURI(marker.label)},${encodeURI(marker.address)}`;
      this.window_open = true;
    },
    toggleClass() {
      this.isHidden = !this.isHidden;
    },
  },
  async created() {
    await this.GetRoadTrip();
  },
})
class RoadTrip extends Vue {
  @Watch('id', { immediate: false, deep: false })
  async onTripChanged() {
    await this.GetRoadTrip();
  }

  @Prop({
    type: [Number, String],
    required: true,
  })
  id;

  @Prop({ type: Boolean })
  showingAll;

  toggleButton() {
    this.showingAll = !this.showingAll;
  }

  @Prop({ type: Boolean })
  showButton;

  async GetRoadTrip() {
    try {
      await axios.get(`${process.env.VUE_APP_API2}trip_detail?id=${this.$route.params.id}`, {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json',
        },
      }).then((response) => {
        this.roadtrip = response.data;

        this.title = `Road Trip No.${this.roadtrip.tripNumber} ${this.roadtrip.title}`;

        if (this.title.length > 60) {
          this.title = `${this.title.substring(0, 57)}...`;
        }

        this.description = `${this.roadtrip.title}`;
        this.url = `https://alabama.travel${this.$router.currentRoute.fullPath}`;
      });
    } catch {
      this.searchText = (this.$route.params.id.replace(/-/g, ' ').replace(/\d+/g, '').trim());
      const filter = { text_search: this.searchText };
      if (this.searchText === '') delete filter.text_search;
      this.$router.push(`/search#stq=road-trips%20${this.searchText}&invalidRoadTrip=true`);
      return;
    }
    setTOC();
    this.$refs.map.$mapPromise.then(async () => {
      await this.handleBoundsChanged();
    });
  }

  async handleBoundsChanged() {
    const bounds = new window.google.maps.LatLngBounds();

    this.roadtrip.map_points.forEach((location) => {
      const newLatLng = new window.google.maps.LatLng(location.latitude, location.longitude);
      bounds.extend(newLatLng);
    });

    if (this.$refs && this.$refs.map && this.$refs.map.$mapObject) {
      this.$refs.map.$mapObject.fitBounds(bounds);
    }
  }

  get Breadcrumbs() {
    return [
      {
        text: 'Road Trips',
        href: '/road-trips',
      },
      {
        text: `Trip #${this.roadtrip.tripNumber}`,
        active: 'page',
      },
    ];
  }
}

export default RoadTrip;
</script>

<style lang="scss" scoped>
.road-trip--detail {
  text-align: left;
}

.road-trip--img {
  overflow: hidden;
}

.road-trip--author,
.road-trip--heading,
.road-trip--h3,
.road-trip--toc-h3 {
  font-family: 'IdealSans-Book', 'Avenir', Helvetica, Arial, sans-serif;
  text-align: center;
}

.road-trip--toc-h3 {
  margin-top: 50px;
}

/* Road Trip Badging */
.road-trip--badge {
  border: 1px solid #036334;
  height: 96px;
  position: absolute;
  width: 77px;
  left: -90px;
}

.road-trip--badge-title {
  bottom: 18px;
  display: block;
  font-size: 1.5rem;
  position: absolute;
  left: -8px;
  text-transform: uppercase;
  transform: rotate(-90deg);
}

.road-trip--badge-number {
  bottom: 0;
  font-family: 'klinicslabbold';
  font-size: 2.5rem;
  position: absolute;
  right: 4px;
}

.road-trip--about {
  overflow: scroll;
  padding: 50px;
  max-height: 50vh;
}

.road-trip--author {
  font-size: 1.25rem;
}

.road-trip--detail {
  margin-top: 70px;
}

.road-trip--detail-header {
  position: relative;
}

.road-trip--heading {
  font-size: 3rem;
}

.road-trip--btn {
  text-align: center;
  margin: 25px 0 75px;
}

.road-trip--content {
  font-family: 'IdealSans-Book', 'Avenir', Helvetica, Arial, sans-serif;
  margin-top: 75px;
  margin-bottom: 75px;
  overflow: hidden;
  text-align: left;
}

.content-hidden {
  font-family: 'IdealSans-Book', 'Avenir', Helvetica, Arial, sans-serif;
  margin-bottom: 75px;
  max-height: 675px;
  overflow: hidden;
  text-align: left;
}

.hidden-content-div {
  box-shadow: 0px -16px 10px -20px #111;
  width: 100%;
}

.btn-center {
  text-align: center;
  margin: 0px 20px;
}

.content-displayed {
  font-family: 'IdealSans-Book', 'Avenir', Helvetica, Arial, sans-serif;
  margin-bottom: 75px;
  max-height: none;
  overflow: hidden;
  text-align: left;
}

.road-trip--directions {
  background-color: #F7F6F1;
  display: flex;
  text-align: left;

  @media screen and (max-width: 667px) {
    display: flex;
    flex-direction: column;
  }

}

.road-trip--map,
.road-trip--about,
.vue-map-container {

  @media screen and (max-width: 667px) {
    max-height: none;
    width: 100vw !important;
  }

  h2 {
    margin-bottom: 30px;
  }

}

.road-trip--dontmiss-image {
  width: 100%;
}

.plus-icon-size {
  font-size: 10px;
}

button>a {
  color: #fff;
}
</style>
<style scoped>
div .road-trip--content>>>a {
  text-decoration: underline !important;
}

div .road-trip--content>>>a:hover {
  color: #000;
  text-decoration: none !important;
}

div .road-trip--content>>>a:focus {
  color: #000;
}
</style>
